import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {TwoPaneSelectList} from 'shells/categorized-select-list/two-pane-select-list';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';
import {Body} from 'shells/typography/body';

import type {GetStagesetsUnarchived_stagesets as StagesetUnarchived} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';
import {addNotification} from '../../../notifications/index';
import {bustManagedCollectionCache} from '../../../util/bust-managed-collection-cache';

import {
    useGetStagesetStarters,
    useCreateStagesetStarter,
    useCreateBlankStageset,
    useDuplicateStagesetForModal,
} from '../graphql/hooks';

import {START_FROM_SCRATCH_CATEGORY} from './consts';
import {getPipelineModalSaveButtonText, getPreviewGraphicUrl} from './helpers';
import {PipelineSelectExisting} from './pipeline-select-existing';
import type {StagesetStarterCategory} from './types';

import styles from './create-pipeline-modal.module.css';

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

export function CreatePipelineModal(props: Props) {
    const [selectedCategory, setSelectedCategory] = React.useState<StagesetStarterCategory>(
        START_FROM_SCRATCH_CATEGORY
    );
    const [selectedStageset, setSelectedStageset] = React.useState<
        StagesetUnarchived | null | undefined
    >(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    const {categories, isLoading} = useGetStagesetStarters();
    const {createStagesetStarter, isLoading: isSaving} = useCreateStagesetStarter();
    const {createBlankStageset, isLoading: isCreatingBlankStageset} = useCreateBlankStageset();
    const {duplicateStageset, isLoading: isDuplicatingStageset} = useDuplicateStagesetForModal();

    // Clear error when modal closes or category changes
    React.useEffect(() => {
        if (!props.isOpen) {
            setErrorMessage(null);
        }
    }, [props.isOpen]);

    React.useEffect(() => {
        setErrorMessage(null);
    }, [selectedCategory]);

    const handleCreateStageset = async () => {
        setErrorMessage(null);

        if (selectedCategory.id === 'START_FROM_SCRATCH') {
            return createBlankStageset({
                name: 'Untitled pipeline',
            })
                .then((result) => {
                    if (result && result.data && result.data.createStageset) {
                        return result.data.createStageset.stageset.htmlUrlPath;
                    }
                })
                .catch((error) => {
                    setErrorMessage(safelyGetGraphQLErrorMessage(error));
                    throw error;
                });
        } else if (selectedCategory.id === 'COPY_EXISTING' && selectedStageset) {
            return duplicateStageset({
                stageset: {
                    id: selectedStageset.id,
                    name: `Copy of ${selectedStageset.name}`,
                },
            })
                .then((result) => {
                    if (result && result.data && result.data.duplicateStageset) {
                        return result.data.duplicateStageset.stageset.htmlUrlPath;
                    }
                })
                .catch((error) => {
                    setErrorMessage(safelyGetGraphQLErrorMessage(error));
                    throw error;
                });
        } else if (
            selectedCategory.collection &&
            // @ts-expect-error
            selectedCategory.collection[0].type !== 'START_FROM_SCRATCH' &&
            // @ts-expect-error
            selectedCategory.collection[0].type !== 'COPY_EXISTING'
        ) {
            // @ts-expect-error
            return createStagesetStarter({type: selectedCategory.collection[0].type})
                .then((result) => {
                    if (result && result.data && result.data.stagesetStarterCreate) {
                        const htmlUrlPath = result.data.stagesetStarterCreate.stageset.htmlUrlPath;
                        // ensure redux state is updated with newly created activity types
                        bustManagedCollectionCache('activityTypes');

                        return htmlUrlPath;
                    }
                })
                .catch((error) => {
                    setErrorMessage(safelyGetGraphQLErrorMessage(error));
                    throw error;
                });
        }
    };

    const collectionCategory =
        selectedCategory.collection && selectedCategory.collection[0]
            ? selectedCategory.collection[0]
            : null;

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onClose}
            headerText='Create a new pipeline'
            isWide={true}
            maxHeight={751}
        >
            <div className={styles['content-container']}>
                <TwoPaneSelectList
                    isLoading={isLoading}
                    categorizedLists={categories}
                    onSelectCategory={setSelectedCategory}
                    selectedCategory={selectedCategory}
                    rightPanelContent={
                        <div className={styles['right-panel-container']}>
                            {collectionCategory && (
                                <>
                                    <div className={styles.header}>
                                        <Body
                                            size='xlarge'
                                            isBold={true}
                                            useDoyle={true}
                                            color='inherit'
                                        >
                                            {collectionCategory.name}
                                        </Body>
                                        <Body color='inherit'>
                                            {collectionCategory.description}
                                        </Body>
                                    </div>
                                    {collectionCategory.type === 'COPY_EXISTING' ? (
                                        <PipelineSelectExisting
                                            selectedStageset={selectedStageset}
                                            onSelect={setSelectedStageset}
                                        />
                                    ) : (
                                        <img
                                            src={getPreviewGraphicUrl(collectionCategory.type)}
                                            alt={collectionCategory.name}
                                            className={styles['preview-graphic']}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    }
                />
            </div>
            <FooterBar
                hasBorder={true}
                fitHeight={true}
                left={
                    <Button variant='text' size='big' onClick={props.onClose} noPadding={true}>
                        Cancel
                    </Button>
                }
                right={
                    <div className='flex gap-16 align-center'>
                        {errorMessage ? (
                            <div className={styles['error-message']}>
                                <Body color='error'>{errorMessage}</Body>
                            </div>
                        ) : undefined}
                        <SaveButton
                            size='big'
                            savingText='Creating pipeline...'
                            buttonText={getPipelineModalSaveButtonText(
                                selectedCategory,
                                selectedStageset
                            )}
                            errorSaving={Boolean(errorMessage)}
                            isSaving={isSaving || isCreatingBlankStageset || isDuplicatingStageset}
                            // @ts-expect-error
                            onClick={() => {
                                handleCreateStageset()
                                    .then((htmlUrlPath) => {
                                        if (htmlUrlPath) {
                                            appHistory.push(htmlUrlPath);
                                            props.onClose();
                                        }
                                    })
                                    .catch(() => {
                                        addNotification('An unexpected error occurred', {
                                            icon: 'frowny',
                                            body: 'Please try again later.',
                                        });
                                    });
                            }}
                            disabled={
                                !selectedCategory ||
                                (selectedCategory.id === 'COPY_EXISTING' && !selectedStageset)
                            }
                        />
                    </div>
                }
            />
        </Modal>
    );
}
